import { cn } from '@heidi/utils';
import type { ButtonProps, ButtonVariant } from '../button';

import type { ToastTone } from './toast.types';

/**
 * Solid-toast recipe is tone-agnostic: an outline-bordered Button that
 * inherits the toast's `color` via `text-current` (and `border-current` for
 * the `subtle` tone, where the toast's surface colour is the same as the
 * Button's default border colour).
 */
export const getSolidActionButtonProps = (
  tone: ToastTone
): Pick<ButtonProps, 'variant' | 'className'> => ({
  variant: 'outline',
  className: cn('text-current', tone === 'subtle' && 'border-current'),
});

/**
 * Outline-toast recipe is per-tone: the toast surface is light/neutral, so
 * the action button itself has to carry the colour signal. Mirrors
 * `getOutlineActionButtonProps` from `toast.stories.tsx`.
 */
export const getOutlineActionButtonProps = (
  tone: ToastTone
): Pick<ButtonProps, 'variant' | 'className'> & { variant: ButtonVariant } => {
  switch (tone) {
    case 'success':
      return { variant: 'success' };
    case 'destructive':
      return { variant: 'destructive' };
    case 'pro':
      return { variant: 'pro' };
    case 'practice':
      return { variant: 'pro_plus' };
    case 'warning':
      return {
        variant: 'default',
        className: cn(
          'bg-validation-warning text-text-primary-invert',
          'hover:bg-validation-warning-hover',
          'active:bg-validation-warning-pressed',
          'disabled:bg-validation-warning-disabled'
        ),
      };
    default:
      return { variant: 'default' };
  }
};
